import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  ChevronRight,
  Facebook,
  Globe,
  Heart,
  Home,
  Image as ImageIcon,
  Instagram,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  PlayCircle,
  Send,
  Sparkles,
  User,
  Youtube,
  X,
} from "lucide-react";

const API_BASE = "https://panel.haymestasarim.com/api";
const API_ILETISIM = `${API_BASE}/iletisim/public`;

function detectLang() {
  const l = navigator.language?.slice(0, 2);
  return ["tr", "en", "de"].includes(l) ? l : "tr";
}

const TEXT = {
  tr: {
    title: "Dijital Anıtlar",
    subtitle: "Sevdiklerinizin hatırasını yaşatan özel anı sayfası",
    loading: "Yükleniyor...",
    noData: "Henüz anıt bulunmuyor",
    backHome: "Ana Sayfa",
    detail: "Anıtı Gör",
    born: "Doğum",
    died: "Vefat",
    cemetery: "Mezarlık",
    graveNo: "Mezar No",
    city: "Şehir",
    views: "Görüntülenme",
    messages: "Mesaj",
    media: "Medya",
    life: "Hayat Hikayesi",
    religious: "Dini İçerikler",
    gallery: "Galeri ve Anılar",
    condolences: "Anı Defteri",
    location: "Mezar Konumu",
    familyNote: "Aile Notu",
    sendMessage: "Taziye Mesajı Gönder",
    name: "Adınız Soyadınız",
    message: "Mesajınız",
    messagePlaceholder: "Merhuma Allah'tan rahmet, ailesine sabır ve başsağlığı dilerim...",
    send: "Gönder",
    sent: "Mesajınız gönderildi.",
    error: "Bir hata oluştu.",
    emptyLife: "Henüz hayat hikayesi eklenmemiş.",
    emptyReligious: "Henüz dini içerik eklenmemiş.",
    emptyGallery: "Henüz galeri/anı eklenmemiş.",
    emptyCondolence: "Henüz mesaj yok. İlk mesajı siz yazın.",
    contact: "İletişim",
    follow: "Bizi Takip Edin",
    mapOpen: "Konuma Git",
    readMore: "Devamını Oku",
    readLess: "Kapat",
    watchOnYouTube: "Videoyu İzle",
  },
  en: {
    title: "Digital Memorials",
    subtitle: "A special memorial page preserving the memory of your loved ones",
    loading: "Loading...",
    noData: "No memorials found",
    backHome: "Home",
    detail: "View Memorial",
    born: "Born",
    died: "Died",
    cemetery: "Cemetery",
    graveNo: "Grave No",
    city: "City",
    views: "Views",
    messages: "Messages",
    media: "Media",
    life: "Life Story",
    religious: "Religious Content",
    gallery: "Gallery & Memories",
    condolences: "Guest Book",
    location: "Grave Location",
    familyNote: "Family Note",
    sendMessage: "Send Condolence",
    name: "Your Full Name",
    message: "Your Message",
    messagePlaceholder: "May God have mercy on the deceased and give patience to the family...",
    send: "Send",
    sent: "Your message has been sent.",
    error: "An error occurred.",
    emptyLife: "No life story has been added yet.",
    emptyReligious: "No religious content has been added yet.",
    emptyGallery: "No gallery/memory has been added yet.",
    emptyCondolence: "No messages yet. Be the first to write.",
    contact: "Contact",
    follow: "Follow Us",
    mapOpen: "Open Location",
    readMore: "Read More",
    readLess: "Close",
    watchOnYouTube: "Watch Video",
  },
  de: {
    title: "Digitale Denkmäler",
    subtitle: "Eine besondere Gedenkseite für Ihre Liebsten",
    loading: "Laden...",
    noData: "Keine Denkmäler gefunden",
    backHome: "Startseite",
    detail: "Denkmal ansehen",
    born: "Geboren",
    died: "Verstorben",
    cemetery: "Friedhof",
    graveNo: "Grab Nr.",
    city: "Stadt",
    views: "Aufrufe",
    messages: "Nachrichten",
    media: "Medien",
    life: "Lebensgeschichte",
    religious: "Religiöse Inhalte",
    gallery: "Galerie & Erinnerungen",
    condolences: "Gästebuch",
    location: "Grabstandort",
    familyNote: "Familiennotiz",
    sendMessage: "Beileid senden",
    name: "Ihr vollständiger Name",
    message: "Ihre Nachricht",
    messagePlaceholder: "Möge Gott dem Verstorbenen gnädig sein und der Familie Geduld geben...",
    send: "Senden",
    sent: "Ihre Nachricht wurde gesendet.",
    error: "Ein Fehler ist aufgetreten.",
    emptyLife: "Noch keine Lebensgeschichte hinzugefügt.",
    emptyReligious: "Noch keine religiösen Inhalte hinzugefügt.",
    emptyGallery: "Noch keine Galerie/Erinnerung hinzugefügt.",
    emptyCondolence: "Noch keine Nachrichten vorhanden.",
    contact: "Kontakt",
    follow: "Folgen Sie uns",
    mapOpen: "Standort öffnen",
    readMore: "Mehr lesen",
    readLess: "Schließen",
    watchOnYouTube: "Video ansehen",
  },
};

function safeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function resolveImage(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) return `https://panel.haymestasarim.com${url}`;
  return `https://panel.haymestasarim.com/${url}`;
}

function formatDate(text, picker, lang) {
  if (text) return text;
  if (!picker) return "-";
  try {
    return new Date(picker).toLocaleDateString(lang === "de" ? "de-DE" : lang === "en" ? "en-US" : "tr-TR");
  } catch {
    return "-";
  }
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const reg = /^.*(youtu.be\/|watch\?v=|embed\/|shorts\/)([^#&?]*).*/;
  const match = url.match(reg);
  const id = match && match[2]?.length === 11 ? match[2] : null;
  if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
  if (url.includes("youtube.com/embed/")) return url;
  return null;
}

export default function PublicCemetery() {
  const { kurumId } = useParams();

  const [lang, setLang] = useState(detectLang());
  const TT = TEXT[lang];

  const [loading, setLoading] = useState(true);
  const [memorials, setMemorials] = useState([]);
  const [contact, setContact] = useState({});
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("life");
  const [expanded, setExpanded] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  const [senderName, setSenderName] = useState("");
  const [senderMessage, setSenderMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!kurumId) return;

    setLoading(true);

    Promise.all([
      fetch(`${API_BASE}/public/cemetery/list/${kurumId}`).then(r => r.json()),
      fetch(`${API_ILETISIM}?kurum_id=${kurumId}&lang=${lang}`).then(r => r.json()).catch(() => ({ data: {} })),
    ])
      .then(([cemeteryRes, contactRes]) => {
        setMemorials(cemeteryRes?.ok ? cemeteryRes.data || [] : []);
        setContact(contactRes?.data || {});
      })
      .catch(err => {
        console.error("Public cemetery load error:", err);
      })
      .finally(() => setLoading(false));
  }, [kurumId, lang]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setActiveTab("life");
    setExpanded(null);

    try {
      const res = await fetch(`${API_BASE}/public/cemetery/memorial/${id}`);
      const data = await res.json();

      if (data.ok) {
        setSelected(data.data);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        alert(data.message || TT.error);
      }
    } catch (err) {
      console.error("Detail error:", err);
      alert(TT.error);
    } finally {
      setDetailLoading(false);
    }
  };

  const sendCondolence = async () => {
    if (!senderName.trim() || !senderMessage.trim()) {
      alert(TT.name + " / " + TT.message);
      return;
    }

    setSending(true);

    try {
      const res = await fetch(`${API_BASE}/public/cemetery/condolences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          memorial_id: selected?.memorial?.id,
          sender_name: senderName.trim(),
          message: senderMessage.trim(),
        }),
      });

      const data = await res.json();

      if (data.ok) {
        setToast(data.message || TT.sent);
        setSenderName("");
        setSenderMessage("");

        if (data.data?.status === "approved") {
          setSelected(prev => ({
            ...prev,
            condolences: [data.data, ...(prev.condolences || [])],
          }));
        }

        setTimeout(() => setToast(""), 3000);
      } else {
        alert(data.message || TT.error);
      }
    } catch (err) {
      console.error("Condolence error:", err);
      alert(TT.error);
    } finally {
      setSending(false);
    }
  };

  const m = selected?.memorial;
  const lifeStories = useMemo(() => safeArray(m?.life_stories), [m]);
  const religiousItems = useMemo(() => safeArray(m?.religious_items), [m]);
  const memoriesJson = useMemo(() => safeArray(m?.memories), [m]);
  const galleryTable = selected?.gallery || [];
  const condolences = selected?.condolences || [];

  const allGallery = useMemo(() => {
    const fromMemories = memoriesJson.map((x, index) => ({
      id: `memory-${index}`,
      title: x.title || x.baslik || "",
      description: x.description || x.aciklama || "",
      image: resolveImage(x.image_url || x.image || x.media_url || x.url),
      type: "memory",
    }));

    const fromGallery = galleryTable.map(x => ({
      id: `gallery-${x.id}`,
      title: x.title || "",
      description: x.description || "",
      image: resolveImage(x.media_url || x.image_url),
      type: x.media_type || "image",
    }));

    return [...fromGallery, ...fromMemories].filter(x => x.image);
  }, [galleryTable, memoriesJson]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <div className="loading-text">{TT.loading}</div>
      </div>
    );
  }

  if (selected && m) {
    return (
      <div className="app-container">
        <style>{`
          /* GLOBAL RESET */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          .app-container {
            min-height: 100vh;
            background: #f8fafc;
            font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
            color: #1e293b;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 24px;
          }

          /* HEADERS */
          .home-header {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
            color: white;
            padding: 32px 0 80px;
          }

          .detail-header {
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            color: white;
            padding: 24px 0 48px;
          }

          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            margin-bottom: 32px;
          }

          .back-button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            border-radius: 999px;
            padding: 8px 20px;
            color: white;
            cursor: pointer;
            text-decoration: none;
            font-weight: 600;
            transition: all 0.2s;
          }

          .back-button:hover {
            background: rgba(255,255,255,0.2);
          }

          .lang-switch {
            display: flex;
            gap: 8px;
          }

          .lang-btn {
            border: 1px solid rgba(255,255,255,0.3);
            background: rgba(255,255,255,0.1);
            color: white;
            border-radius: 999px;
            padding: 6px 14px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
          }

          .lang-btn.active {
            background: #f5b642;
            color: #0f172a;
            border-color: #f5b642;
          }

          /* HERO SECTION */
          .hero-section {
            display: flex;
            align-items: center;
            gap: 32px;
            flex-wrap: wrap;
          }

          .hero-avatar {
            flex-shrink: 0;
            width: 180px;
            height: 180px;
            border-radius: 50%;
            background: linear-gradient(135deg, #f5b642, #fbbf24);
            padding: 4px;
          }

          .hero-avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
          }

          .avatar-placeholder {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
          }

          .hero-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(245,182,66,0.15);
            padding: 6px 14px;
            border-radius: 999px;
            font-size: 13px;
            font-weight: 600;
            color: #fbbf24;
            margin-bottom: 16px;
          }

          .hero-title {
            font-size: 42px;
            font-weight: 800;
            margin-bottom: 12px;
            line-height: 1.2;
          }

          .hero-info {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            font-size: 14px;
          }

          .hero-info span {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: rgba(255,255,255,0.1);
            padding: 6px 14px;
            border-radius: 999px;
          }

          .home-hero {
            text-align: center;
            max-width: 700px;
            margin: 40px auto 0;
          }

          .home-hero .hero-title {
            font-size: 48px;
            margin-bottom: 16px;
          }

          .hero-subtitle {
            font-size: 18px;
            opacity: 0.9;
          }

          /* STATS - PROFESSIONAL 2-LI GRID */
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
            margin: -40px 0 24px;
          }

          .stat-card {
            background: white;
            border-radius: 20px;
            padding: 16px;
            display: flex;
            align-items: center;
            gap: 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .stat-icon {
            width: 48px;
            height: 48px;
            background: #fef3c7;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            color: #f5b642;
          }

          .stat-value {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
          }

          .stat-label {
            font-size: 12px;
            color: #64748b;
          }

          /* FAMILY NOTE */
          .family-note {
            background: linear-gradient(135deg, #fffbeb, #fef3c7);
            border-left: 5px solid #f5b642;
            border-radius: 20px;
            padding: 20px;
            margin-bottom: 24px;
          }

          .family-note-label {
            font-size: 12px;
            font-weight: 700;
            color: #b45309;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
          }

          .family-note-text {
            font-size: 16px;
            line-height: 1.6;
            color: #78350f;
            font-style: italic;
          }

          /* TABS - MOBILE 2'LI GRID */
          .tabs-nav {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-bottom: 24px;
            position: sticky;
            top: 0;
            background: #f8fafc;
            padding: 12px 0;
            z-index: 10;
          }

          .tab-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px;
            border: none;
            border-radius: 999px;
            background: white;
            color: #334155;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
            box-shadow: 0 2px 6px rgba(0,0,0,0.05);
          }

          .tab-btn.active {
            background: #0f172a;
            color: white;
          }

          /* CARDS */
          .card {
            background: white;
            border-radius: 24px;
            padding: 20px;
            margin-bottom: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .card-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
          }

          .card-icon {
            width: 44px;
            height: 44px;
            background: #fef3c7;
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #f5b642;
          }

          .card-header h3 {
            font-size: 20px;
            margin: 0;
          }

          .card-text {
            color: #475569;
            line-height: 1.7;
            margin: 0;
          }

          .read-more {
            margin-top: 12px;
            background: none;
            border: none;
            color: #f5b642;
            font-weight: 600;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          /* RELIGIOUS - VIDEO BUTTON (MODAL ILE) */
          .religious-card {
            text-align: center;
          }

          .video-btn {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            background: #ff0000;
            color: white;
            padding: 12px 24px;
            border-radius: 999px;
            text-decoration: none;
            font-weight: 700;
            margin: 16px 0;
            transition: transform 0.2s, background 0.2s;
            border: none;
            cursor: pointer;
          }

          .video-btn:hover {
            background: #cc0000;
            transform: scale(1.02);
          }

          /* GALLERY */
          .gallery-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 20px;
          }

          .gallery-item {
            background: white;
            border-radius: 20px;
            overflow: hidden;
            cursor: pointer;
            transition: transform 0.2s;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .gallery-item:hover {
            transform: translateY(-4px);
          }

          .gallery-item img {
            width: 100%;
            height: 200px;
            object-fit: cover;
          }

          .gallery-caption {
            padding: 12px;
          }

          .gallery-title {
            font-weight: 700;
            margin-bottom: 4px;
          }

          .gallery-desc {
            font-size: 13px;
            color: #64748b;
          }

          /* LOCATION */
          .location-card {
            background: white;
            border-radius: 24px;
            padding: 20px;
            margin-top: 20px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          .location-link {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: #f5b642;
            color: #0f172a;
            padding: 10px 20px;
            border-radius: 999px;
            text-decoration: none;
            font-weight: 700;
            margin-top: 12px;
          }

          /* CONDOLENCE FORM */
          .condolence-form {
            background: white;
            border-radius: 24px;
            padding: 24px;
            margin-bottom: 24px;
          }

          .condolence-form h3 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 20px;
          }

          .form-input, .form-textarea {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid #e2e8f0;
            border-radius: 16px;
            font-size: 15px;
            margin-bottom: 16px;
            font-family: inherit;
          }

          .form-textarea {
            resize: vertical;
          }

          .form-input:focus, .form-textarea:focus {
            outline: none;
            border-color: #f5b642;
          }

          .submit-btn {
            width: 100%;
            background: #0f172a;
            color: white;
            border: none;
            border-radius: 999px;
            padding: 14px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            cursor: pointer;
            transition: background 0.2s;
          }

          .submit-btn:hover {
            background: #1e293b;
          }

          .submit-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          /* CONDOLENCES LIST */
          .condolences-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .condolence-card {
            background: white;
            border-radius: 20px;
            padding: 20px;
            display: flex;
            gap: 16px;
          }

          .condolence-avatar {
            width: 48px;
            height: 48px;
            background: #f5b642;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            font-size: 20px;
            color: #0f172a;
            flex-shrink: 0;
          }

          .condolence-content {
            flex: 1;
          }

          .condolence-name {
            font-weight: 800;
            margin-bottom: 4px;
          }

          .condolence-date {
            font-size: 12px;
            color: #94a3b8;
            margin-bottom: 8px;
          }

          .condolence-message {
            color: #475569;
            line-height: 1.6;
          }

          /* MEMORIALS GRID (HOME) */
          .memorials-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 24px;
            margin-top: -40px;
          }

          .memorial-card {
            background: white;
            border-radius: 28px;
            overflow: hidden;
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
            transition: transform 0.2s;
          }

          .memorial-card:hover {
            transform: translateY(-4px);
          }

          .memorial-image {
            position: relative;
            height: 250px;
            overflow: hidden;
          }

          .memorial-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .image-placeholder {
            width: 100%;
            height: 100%;
            background: #f1f5f9;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
          }

          .image-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7));
          }

          .image-caption {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 20px;
            color: white;
          }

          .image-caption h2 {
            margin: 0 0 6px;
            font-size: 22px;
          }

          .image-caption div {
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .memorial-info {
            padding: 18px;
          }

          .memorial-dates {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #64748b;
            font-size: 13px;
            font-weight: 500;
            margin-bottom: 12px;
          }

          .memorial-stats {
            display: flex;
            gap: 16px;
            font-size: 13px;
            color: #64748b;
            margin-bottom: 16px;
          }

          .memorial-stats span {
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          .detail-btn {
            width: 100%;
            background: #0f172a;
            color: white;
            border: none;
            border-radius: 999px;
            padding: 12px;
            font-weight: 700;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            cursor: pointer;
            transition: background 0.2s;
          }

          .detail-btn:hover {
            background: #1e293b;
          }

          .detail-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          /* FOOTER */
       .footer { 
  background: linear-gradient(
    135deg,
    #0f172a 0%,
    #1e3a8a 45%,
    #2563eb 100%
  );
  color: white; 
  padding: 48px 0 32px; 
  margin-top: 48px;
}

           .footer-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 32px;
          }

          .footer h3 {
            margin-bottom: 16px;
            font-size: 18px;
          }

          .footer-text {
            color: #94a3b8;
            line-height: 1.6;
          }

          .footer-line {
            display: flex;
            align-items: center;
            gap: 10px;
            color: #94a3b8;
            margin-bottom: 12px;
          }

          .footer-line a {
            color: #94a3b8;
            text-decoration: none;
          }

          .footer-line a:hover {
            color: #f5b642;
          }

          .social-links {
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
          }

          .social-link {
            width: 42px;
            height: 42px;
            background: #f5b642;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #0f172a;
            transition: transform 0.2s;
          }

          .social-link:hover {
            transform: scale(1.1);
          }

          /* EMPTY STATE */
          .empty-state {
            background: white;
            border-radius: 28px;
            padding: 60px 32px;
            text-align: center;
            color: #94a3b8;
          }

          .empty-icon {
            margin-bottom: 16px;
          }

          .empty-text {
            font-weight: 600;
          }

          /* TOAST */
          .toast {
            position: fixed;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            background: #10b981;
            color: white;
            padding: 12px 24px;
            border-radius: 999px;
            font-weight: 600;
            z-index: 1000;
            box-shadow: 0 8px 20px rgba(0,0,0,0.2);
          }

          /* LIGHTBOX & MODAL */
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.85);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            cursor: pointer;
          }

          .modal-content {
            position: relative;
            max-width: 90vw;
            max-height: 85vh;
          }

          .modal-close {
            position: absolute;
            top: -40px;
            right: 0;
            background: rgba(0,0,0,0.5);
            border: none;
            color: white;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 20px;
          }

          .modal-close:hover {
            background: rgba(0,0,0,0.7);
          }

          .modal-iframe {
            width: 80vw;
            height: 45vw;
            max-width: 800px;
            max-height: 450px;
            border: none;
            border-radius: 16px;
          }

          .modal-img {
            max-width: 90vw;
            max-height: 85vh;
            border-radius: 16px;
          }

          /* LOADING */
          .loading-container {
            min-height: 100vh;
            background: linear-gradient(135deg, #0f172a, #1e293b);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
          }

          .loading-spinner {
            width: 50px;
            height: 50px;
            border: 4px solid rgba(255,255,255,0.2);
            border-top-color: #f5b642;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 20px;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .loading-text {
            font-size: 18px;
            font-weight: 600;
          }

          /* RESPONSIVE */
          @media (max-width: 768px) {
            .container {
              padding: 0 16px;
            }

            .hero-section {
              flex-direction: column;
              text-align: center;
            }

            .hero-info {
              justify-content: center;
            }

            .hero-title {
              font-size: 32px;
            }

            .home-hero .hero-title {
              font-size: 36px;
            }

            .gallery-grid {
              grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
            }

            .condolence-card {
              flex-direction: column;
              align-items: center;
              text-align: center;
            }

            .footer-grid {
              text-align: center;
            }

            .footer-line {
              justify-content: center;
            }

            .social-links {
              justify-content: center;
            }

            .modal-iframe {
              width: 95vw;
              height: 55vw;
            }
          }
        `}</style>

        <header className="detail-header">
          <div className="container">
            <div className="header-top">
              <button onClick={() => setSelected(null)} className="back-button">
                <ArrowLeft size={18} /> {TT.backHome}
              </button>
              <div className="lang-switch">
                {["tr", "en", "de"].map(l => (
                  <button
                    key={l}
                    onClick={() => setLang(l)}
                    className={`lang-btn ${lang === l ? "active" : ""}`}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            <div className="hero-section">
              <div className="hero-avatar">
                {m.photo_url ? (
                  <img src={resolveImage(m.photo_url)} alt={m.fullname} />
                ) : (
                  <div className="avatar-placeholder"><User size={80} /></div>
                )}
              </div>
              <div className="hero-content">
                <div className="hero-badge"><Sparkles size={16} /> {TEXT[lang].title}</div>
                <h1 className="hero-title">{m.fullname}</h1>
                <div className="hero-info">
                  <span><Calendar size={16} /> {formatDate(m.birth_date_text, m.birth_date_picker, lang)} - {formatDate(m.death_date_text, m.death_date_picker, lang)}</span>
                  {m.cemetery_name && <span><MapPin size={16} /> {m.cemetery_name}</span>}
                  {m.grave_no && <span>{TT.graveNo}: {m.grave_no}</span>}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">👁</div>
              <div><div className="stat-value">{m.view_count || 0}</div><div className="stat-label">{TT.views}</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><MessageCircle size={24} /></div>
              <div><div className="stat-value">{m.condolences_count || condolences.length}</div><div className="stat-label">{TT.messages}</div></div>
            </div>
          </div>

          {m.family_note && (
            <div className="family-note">
              <div className="family-note-label">{TT.familyNote}</div>
              <div className="family-note-text">“{m.family_note}”</div>
            </div>
          )}

          <nav className="tabs-nav">
            <button onClick={() => setActiveTab("life")} className={`tab-btn ${activeTab === "life" ? "active" : ""}`}>
              <BookOpen size={17} /> {TT.life}
            </button>
            <button onClick={() => setActiveTab("religious")} className={`tab-btn ${activeTab === "religious" ? "active" : ""}`}>
              <Youtube size={17} /> {TT.religious}
            </button>
            <button onClick={() => setActiveTab("gallery")} className={`tab-btn ${activeTab === "gallery" ? "active" : ""}`}>
              <ImageIcon size={17} /> {TT.gallery}
            </button>
            <button onClick={() => setActiveTab("condolences")} className={`tab-btn ${activeTab === "condolences" ? "active" : ""}`}>
              <Heart size={17} /> {TT.condolences}
            </button>
          </nav>

          {activeTab === "life" && (
            <div className="section">
              {lifeStories.length === 0 ? <Empty text={TT.emptyLife} icon={<BookOpen size={44} />} /> : (
                <div className="stories-grid">
                  {lifeStories.map((story, i) => {
                    const content = story.content || story.text || story.description || "";
                    const title = story.title || story.baslik || TT.life;
                    const isLong = content.length > 360;
                    const showFull = expanded === i;

                    return (
                      <article key={i} className="card">
                        <div className="card-header">
                          <div className="card-icon"><BookOpen size={21} /></div>
                          <h3>{title}</h3>
                        </div>
                        <p className="card-text">
                          {showFull || !isLong ? content : `${content.slice(0, 360)}...`}
                        </p>
                        {isLong && (
                          <button onClick={() => setExpanded(showFull ? null : i)} className="read-more">
                            {showFull ? TT.readLess : TT.readMore} <ChevronRight size={16} />
                          </button>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "religious" && (
            <div className="section">
              {religiousItems.length === 0 ? <Empty text={TT.emptyReligious} icon={<Youtube size={44} />} /> : (
                <div className="religious-grid">
                  {religiousItems.map((item, i) => {
                    const embedUrl = getYouTubeEmbedUrl(item.link || item.url || item.video_url);
                    const title = item.title || item.baslik || item.type || TT.religious;

                    return (
                      <article key={i} className="card religious-card">
                        <div className="card-header">
                          <div className="card-icon"><PlayCircle size={23} /></div>
                          <h3>{title}</h3>
                        </div>

                        {embedUrl && (
                          <button onClick={() => setVideoModal(embedUrl)} className="video-btn">
                            <Youtube size={24} />
                            <span>{TT.watchOnYouTube}</span>
                          </button>
                        )}

                        {(item.text || item.description || item.aciklama) && (
                          <p className="card-text">
                            {item.text || item.description || item.aciklama}
                          </p>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "gallery" && (
            <div className="section">
              {allGallery.length === 0 ? <Empty text={TT.emptyGallery} icon={<ImageIcon size={44} />} /> : (
                <div className="gallery-grid">
                  {allGallery.map(item => (
                    <div key={item.id} onClick={() => setLightbox(item.image)} className="gallery-item">
                      <img src={item.image} alt={item.title} />
                      {(item.title || item.description) && (
                        <div className="gallery-caption">
                          {item.title && <div className="gallery-title">{item.title}</div>}
                          {item.description && <div className="gallery-desc">{item.description}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {m.location_address && activeTab !== "condolences" && (
            <div className="location-card">
              <div className="card-header">
                <div className="card-icon"><MapPin size={22} /></div>
                <h3>{TT.location}</h3>
              </div>
              <a href={m.location_address} target="_blank" rel="noreferrer" className="location-link">
                {TT.mapOpen} <MapPin size={17} />
              </a>
            </div>
          )}

          {activeTab === "condolences" && (
            <div className="section">
              <div className="condolence-form">
                <h3><Heart size={22} /> {TT.sendMessage}</h3>
                <input
                  value={senderName}
                  onChange={e => setSenderName(e.target.value)}
                  placeholder={TT.name}
                  className="form-input"
                />
                <textarea
                  value={senderMessage}
                  onChange={e => setSenderMessage(e.target.value)}
                  placeholder={TT.messagePlaceholder}
                  rows={4}
                  className="form-textarea"
                />
                <button onClick={sendCondolence} disabled={sending} className="submit-btn">
                  <Send size={18} /> {TT.send}
                </button>
              </div>

              <div className="condolences-list">
                {condolences.length === 0 ? <Empty text={TT.emptyCondolence} icon={<MessageCircle size={44} />} /> : condolences.map(c => (
                  <div key={c.id} className="condolence-card">
                    <div className="condolence-avatar">
                      {c.sender_name?.slice(0, 1)?.toUpperCase() || "?"}
                    </div>
                    <div className="condolence-content">
                      <div className="condolence-name">{c.sender_name}</div>
                      <div className="condolence-date">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                      </div>
                      <div className="condolence-message">{c.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>

        <Footer contact={contact} TT={TT} />

        {toast && <Toast text={toast} />}

        {/* Lightbox for Images */}
        {lightbox && (
          <div onClick={() => setLightbox(null)} className="modal-overlay">
            <div className="modal-content">
              <button onClick={() => setLightbox(null)} className="modal-close"><X size={18} /></button>
              <img src={lightbox} alt="" className="modal-img" />
            </div>
          </div>
        )}

        {/* Modal for YouTube Videos */}
        {videoModal && (
          <div onClick={() => setVideoModal(null)} className="modal-overlay">
            <div className="modal-content">
              <button onClick={() => setVideoModal(null)} className="modal-close"><X size={18} /></button>
              <iframe src={videoModal} className="modal-iframe" allowFullScreen title="YouTube Video Player" allow="autoplay; encrypted-media" />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-container">
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .app-container { min-height: 100vh; background: #f8fafc; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1e293b; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
        .home-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%); color: white; padding: 32px 0 80px; }
        .header-top { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; margin-bottom: 32px; }
        .back-button { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 999px; padding: 8px 20px; color: white; cursor: pointer; text-decoration: none; font-weight: 600; transition: all 0.2s; }
        .back-button:hover { background: rgba(255,255,255,0.2); }
        .lang-switch { display: flex; gap: 8px; }
        .lang-btn { border: 1px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.1); color: white; border-radius: 999px; padding: 6px 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .lang-btn.active { background: #f5b642; color: #0f172a; border-color: #f5b642; }
        .home-hero { text-align: center; max-width: 700px; margin: 40px auto 0; }
        .hero-title { font-size: 48px; font-weight: 800; margin-bottom: 16px; line-height: 1.2; }
        .hero-subtitle { font-size: 18px; opacity: 0.9; }
        .memorials-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 24px; margin-top: -40px; }
        .memorial-card { background: white; border-radius: 28px; overflow: hidden; box-shadow: 0 8px 24px rgba(0,0,0,0.08); transition: transform 0.2s; }
        .memorial-card:hover { transform: translateY(-4px); }
        .memorial-image { position: relative; height: 250px; overflow: hidden; }
        .memorial-image img { width: 100%; height: 100%; object-fit: cover; }
        .image-placeholder { width: 100%; height: 100%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; color: #94a3b8; }
        .image-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7)); }
        .image-caption { position: absolute; bottom: 0; left: 0; right: 0; padding: 20px; color: white; }
        .image-caption h2 { margin: 0 0 6px; font-size: 22px; }
        .image-caption div { font-size: 13px; display: flex; align-items: center; gap: 4px; }
        .memorial-info { padding: 18px; }
        .memorial-dates { display: flex; align-items: center; gap: 6px; color: #64748b; font-size: 13px; font-weight: 500; margin-bottom: 12px; }
        .memorial-stats { display: flex; gap: 16px; font-size: 13px; color: #64748b; margin-bottom: 16px; }
        .memorial-stats span { display: inline-flex; align-items: center; gap: 4px; }
        .detail-btn { width: 100%; background: #0f172a; color: white; border: none; border-radius: 999px; padding: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background 0.2s; }
        .detail-btn:hover { background: #1e293b; }
        .detail-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .empty-state { background: white; border-radius: 28px; padding: 60px 32px; text-align: center; color: #94a3b8; }
        .empty-icon { margin-bottom: 16px; }
        .empty-text { font-weight: 600; }
  .footer { 
  background: linear-gradient(
    135deg,
    #0f172a 0%,
    #1e3a8a 45%,
    #2563eb 100%
  );
  color: white; 
  padding: 48px 0 32px; 
  margin-top: 48px;
}
        .footer-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 32px; }
        .footer h3 { margin-bottom: 16px; font-size: 18px; }
        .footer-text { color: #94a3b8; line-height: 1.6; }
        .footer-line { display: flex; align-items: center; gap: 10px; color: #94a3b8; margin-bottom: 12px; }
        .footer-line a { color: #94a3b8; text-decoration: none; }
        .footer-line a:hover { color: #f5b642; }
        .social-links { display: flex; gap: 12px; flex-wrap: wrap; }
        .social-link { width: 42px; height: 42px; background: #f5b642; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #0f172a; transition: transform 0.2s; }
        .social-link:hover { transform: scale(1.1); }
        @media (max-width: 768px) {
          .container { padding: 0 16px; }
          .hero-title { font-size: 36px; }
          .memorials-grid { grid-template-columns: 1fr; }
          .footer-grid { text-align: center; }
          .footer-line { justify-content: center; }
          .social-links { justify-content: center; }
        }
      `}</style>

      <header className="home-header">
        <div className="container">
          <div className="header-top">
            <Link to="/" className="back-button">
              <Home size={17} /> {TT.backHome}
            </Link>
            <div className="lang-switch">
              {["tr", "en", "de"].map(l => (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className={`lang-btn ${lang === l ? "active" : ""}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="home-hero">
            <h1 className="hero-title">{contact.kurum_adi || TT.title}</h1>
            <p className="hero-subtitle">{TT.subtitle}</p>
          </div>
        </div>
      </header>

      <main className="container">
        {memorials.length === 0 ? (
          <Empty text={TT.noData} icon={<Heart size={52} />} />
        ) : (
          <div className="memorials-grid">
            {memorials.map(item => (
              <article key={item.id} className="memorial-card">
                <div className="memorial-image">
                  {item.photo_url ? (
                    <img src={resolveImage(item.photo_url)} alt={item.fullname} />
                  ) : (
                    <div className="image-placeholder"><User size={70} /></div>
                  )}
                  <div className="image-overlay" />
                  <div className="image-caption">
                    <h2>{item.fullname}</h2>
                    {item.cemetery_name && <div><MapPin size={15} /> {item.cemetery_name}</div>}
                  </div>
                </div>

                <div className="memorial-info">
                  <div className="memorial-dates">
                    <Calendar size={16} />
                    {formatDate(item.birth_date_text, item.birth_date_picker, lang)} - {formatDate(item.death_date_text, item.death_date_picker, lang)}
                  </div>

                  <div className="memorial-stats">
                    <span>👁 {item.view_count || 0}</span>
                    <span><MessageCircle size={14} /> {item.condolences_count || 0}</span>
                  </div>

                  <button onClick={() => openDetail(item.id)} disabled={detailLoading} className="detail-btn">
                    {TT.detail} <ChevronRight size={17} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      <Footer contact={contact} TT={TT} />
    </div>
  );
}

function Footer({ contact, TT }) {
  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <h3>{contact.kurum_adi || TT.title}</h3>
          <p className="footer-text">{TT.subtitle}</p>
        </div>

        <div>
          <h3>{TT.contact}</h3>
          {(contact.adres || contact.adres_tr || contact.address) && (
            <p className="footer-line"><MapPin size={16} /> {contact.adres || contact.adres_tr || contact.address}</p>
          )}
          {contact.telefon && (
            <p className="footer-line"><Phone size={16} /> <a href={`tel:${contact.telefon}`}>{contact.telefon}</a></p>
          )}
          {contact.email && (
            <p className="footer-line"><Mail size={16} /> <a href={`mailto:${contact.email}`}>{contact.email}</a></p>
          )}
        </div>

        <div>
          <h3>{TT.follow}</h3>
          <div className="social-links">
            {contact.instagram && <Social href={contact.instagram} icon={<Instagram size={20} />} />}
            {contact.facebook && <Social href={contact.facebook} icon={<Facebook size={20} />} />}
            {contact.youtube && <Social href={contact.youtube} icon={<Youtube size={20} />} />}
            {contact.website && <Social href={contact.website} icon={<Globe size={20} />} />}
          </div>
        </div>
      </div>
    </footer>
  );
}

function Social({ href, icon }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="social-link">
      {icon}
    </a>
  );
}

function Empty({ text, icon }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <div className="empty-text">{text}</div>
    </div>
  );
}

function Toast({ text }) {
  return <div className="toast">{text}</div>;
}